import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import StoreService from '../services/storeService';
import PermissionService from '../services/permissionService';
import api from '../services/api';
import requestDeduplicator from '../utils/requestDeduplicator';
import { isSubdomainStoreMode } from '../utils/storeUtils';

export const AppDataContext = createContext(null);

const isCustomerPath = () => isSubdomainStoreMode() || window.location.pathname.startsWith('/stores/');

export const AppDataProvider = ({ children }) => {
  const { 
    isManagementAuthenticated, managementUser,
    isCustomerAuthenticated, customerUser
  } = useAuth();
  
  // Stores data (management only)
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesError, setStoresError] = useState(null);
  const [storesLoaded, setStoresLoaded] = useState(false);
  
  // Current store selection
  const [currentStoreIndex, setCurrentStoreIndex] = useState(0);
  const [isStoreSelectionHydrated, setIsStoreSelectionHydrated] = useState(false);
  const currentStore = isStoreSelectionHydrated && stores.length > 0 ? stores[currentStoreIndex] : null;

  useEffect(() => {
    if (isCustomerPath()) {
      setIsStoreSelectionHydrated(false);
      return;
    }

    if (stores.length === 0) {
      setIsStoreSelectionHydrated(false);
      return;
    }

    const storedStoreId = localStorage.getItem('currentStoreId');
    if (!storedStoreId) {
      setIsStoreSelectionHydrated(true);
      return;
    }

    const storedStoreIndex = stores.findIndex((store) => store.id === storedStoreId);
    if (storedStoreIndex === -1) {
      setIsStoreSelectionHydrated(true);
      return;
    }

    if (storedStoreIndex !== currentStoreIndex) {
      setCurrentStoreIndex(storedStoreIndex);
      return;
    }

    setIsStoreSelectionHydrated(true);
  }, [currentStoreIndex, stores]);

  // Keep API store header source in sync with selected management store
  useEffect(() => {
    if (isStoreSelectionHydrated && currentStore?.id) {
      localStorage.setItem('currentStoreId', currentStore.id);
    }
  }, [currentStore?.id, isStoreSelectionHydrated]);
  
  // Management preferences
  const [managementPreferences, setManagementPreferences] = useState(null);
  const [managementPreferencesLoading, setManagementPreferencesLoading] = useState(false);
  const [managementPreferencesError, setManagementPreferencesError] = useState(null);
  const [managementPreferencesLoaded, setManagementPreferencesLoaded] = useState(false);
  const managementProfileInFlightRef = useRef(false);

  // Customer preferences
  const [customerPreferences, setCustomerPreferences] = useState(null);
  const [customerPreferencesLoading, setCustomerPreferencesLoading] = useState(false);
  const [customerPreferencesError, setCustomerPreferencesError] = useState(null);
  const [customerPreferencesLoaded, setCustomerPreferencesLoaded] = useState(false);
  const customerProfileInFlightRef = useRef(false);

  // Permissions data - stored as Map<storeId, Set<permissions>> (management only)
  const [permissions, setPermissions] = useState(new Map());
  const [storeOwnership, setStoreOwnership] = useState(new Map());
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const permissionsInFlightRef = useRef(false);
  const permissionsHydrationRetryRef = useRef(false);

  // Load stores data (management only)
  const loadStores = useCallback(async () => {
    // Customer routes must use store overview endpoint, never management basic-details
    if (isCustomerPath()) return;
    if (storesLoaded || !isManagementAuthenticated || !managementUser) return;
    
    try {
      setStoresLoading(true);
      setStoresError(null);
      const storesData = await StoreService.getStores();
      
      const transformedStores = storesData.map(store => ({
        id: store.storeId,
        name: store.storeName,
        slug: store.slug,
        disabled: store.disabled,
        country: store.country ?? null,
        brandColors: store.brandColors ?? null,
        managementLogoUrl: store.managementLogoUrl ?? null,
        faviconUrl: store.faviconUrl ?? null,
      }));
      
      setStores(transformedStores);
      setStoresLoaded(true);
    } catch (err) {
      setStoresError(err.message || 'Failed to fetch stores');
      console.error('Error fetching stores:', err);
    } finally {
      setStoresLoading(false);
    }
  }, [storesLoaded, isManagementAuthenticated, managementUser]);

  // Load management user preferences
  const loadManagementPreferences = useCallback(async () => {
    if (isCustomerPath()) return;
    if (managementPreferencesLoaded || !isManagementAuthenticated) return;
    if (managementProfileInFlightRef.current) return;
    managementProfileInFlightRef.current = true;
    
    try {
      setManagementPreferencesLoading(true);
      setManagementPreferencesError(null);
      const response = await requestDeduplicator.deduplicate(
        'management-user-profile',
        () => api.get('/users/profile', { _authContext: 'management' })
      );
      setManagementPreferences(response.data);
      setManagementPreferencesLoaded(true);
    } catch (err) {
      setManagementPreferencesError(err.message || 'Failed to fetch user profile');
      console.error('Error fetching management user profile:', err);
      
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('management_auth_token');
        localStorage.removeItem('management_user_data');
      }
    } finally {
      setManagementPreferencesLoading(false);
      managementProfileInFlightRef.current = false;
    }
  }, [managementPreferencesLoaded, isManagementAuthenticated]);

  // Load customer user preferences
  const loadCustomerPreferences = useCallback(async () => {
    if (!isCustomerPath()) return;
    if (customerPreferencesLoaded || !isCustomerAuthenticated) return;
    if (customerProfileInFlightRef.current) return;
    customerProfileInFlightRef.current = true;
    
    try {
      setCustomerPreferencesLoading(true);
      setCustomerPreferencesError(null);
      const response = await requestDeduplicator.deduplicate(
        'customer-user-profile',
        () => api.get('/users/profile', { _authContext: 'customer' })
      );
      setCustomerPreferences(response.data);
      setCustomerPreferencesLoaded(true);
    } catch (err) {
      setCustomerPreferencesError(err.message || 'Failed to fetch customer profile');
      console.error('Error fetching customer user profile:', err);
      
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('customer_auth_token');
        localStorage.removeItem('customer_user_data');
      }
    } finally {
      setCustomerPreferencesLoading(false);
      customerProfileInFlightRef.current = false;
    }
  }, [customerPreferencesLoaded, isCustomerAuthenticated]);

  // Update user preferences (path-aware: updates the correct context)
  const updateUserPreferences = async (preferences) => {
    try {
      const response = await api.put('/users/preferences', preferences);
      if (isCustomerPath()) {
        setCustomerPreferences(prev => ({ ...prev, ...response.data }));
      } else {
        setManagementPreferences(prev => ({ ...prev, ...response.data }));
      }
      return response.data;
    } catch (err) {
      console.error('Error updating user preferences:', err);
      throw err;
    }
  };

  // Update user profile picture (path-aware)
  const updateUserProfilePicture = (pictureUrl) => {
    if (isCustomerPath()) {
      setCustomerPreferences(prev => ({ ...prev, picture: pictureUrl }));
    } else {
      setManagementPreferences(prev => ({ ...prev, picture: pictureUrl }));
    }
  };

  const updateUserPhone = (phone) => {
    if (isCustomerPath()) {
      setCustomerPreferences(prev => ({ ...prev, phone }));
    } else {
      setManagementPreferences(prev => ({ ...prev, phone }));
    }
  };

  // Refresh stores data
  const refreshStores = async () => {
    if (isCustomerPath()) return;
    try {
      setStoresLoading(true);
      setStoresError(null);
      const storesData = await StoreService.getStores();
      
      const transformedStores = storesData.map(store => ({
        id: store.storeId,
        name: store.storeName,
        slug: store.slug,
        disabled: store.disabled,
        country: store.country ?? null,
        brandColors: store.brandColors ?? null,
        managementLogoUrl: store.managementLogoUrl ?? null,
        faviconUrl: store.faviconUrl ?? null,
      }));
      
      setStores(transformedStores);
      setStoresLoaded(true);
    } catch (err) {
      setStoresError(err.message || 'Failed to fetch stores');
      console.error('Error fetching stores:', err);
    } finally {
      setStoresLoading(false);
    }
  };

  // Refresh user preferences (path-aware)
  const refreshUserPreferences = async () => {
    if (isCustomerPath()) {
      setCustomerPreferencesLoaded(false);
      await loadCustomerPreferences();
    } else {
      setManagementPreferencesLoaded(false);
      await loadManagementPreferences();
    }
  };

  // Load permissions for all stores (management only)
  const loadPermissions = useCallback(async () => {
    if (permissionsLoaded || !isManagementAuthenticated || !managementUser) return;
    if (permissionsInFlightRef.current) return;
    
    const userType = managementUser.userType || managementUser.user_type;
    
    // App admins have all permissions - no need to load
    if (userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin') {
      setPermissionsLoaded(true);
      setPermissionsError(null);
      return;
    }

    const userId = managementUser.id || managementUser.userId;
    // Wait for stores before loading; do not mark loaded with empty permissions
    // (that permanently hides menus after set-password auto-login races).
    if (!userId || !stores || stores.length === 0) {
      return;
    }

    permissionsInFlightRef.current = true;
    try {
      setPermissionsLoading(true);
      setPermissionsError(null);
      
      const teamMemberPermissions = await PermissionService.getTeamMemberOwnPermissions();
      
      const permissionsMap = new Map();
      const ownershipMap = new Map();
      
      if (teamMemberPermissions && teamMemberPermissions.storesPermissions) {
        const storesPermissions = teamMemberPermissions.storesPermissions || teamMemberPermissions.StoresPermissions;
        if (storesPermissions && Array.isArray(storesPermissions)) {
          storesPermissions.forEach(storePerm => {
            const storeId = storePerm.storeId || storePerm.StoreId;
            const permissionsArray = storePerm.permissions || storePerm.Permissions;
            const isOwner = Boolean(storePerm.isOwner ?? storePerm.IsOwner);
            if (storeId && permissionsArray && Array.isArray(permissionsArray)) {
              permissionsMap.set(storeId, new Set(permissionsArray));
              ownershipMap.set(storeId, isOwner);
            }
          });
        }
      }
      
      setPermissions(permissionsMap);
      setStoreOwnership(ownershipMap);
      setPermissionsLoaded(true);
    } catch (err) {
      setPermissionsError(err.message || 'Failed to fetch permissions');
      console.error('Error fetching permissions:', err);
    } finally {
      setPermissionsLoading(false);
      permissionsInFlightRef.current = false;
    }
  }, [permissionsLoaded, isManagementAuthenticated, managementUser, stores]);

  // Refresh permissions
  const refreshPermissions = useCallback(async () => {
    permissionsInFlightRef.current = false;
    permissionsHydrationRetryRef.current = false;
    setPermissionsLoaded(false);
    setPermissionsError(null);
    setPermissions(new Map());
    setStoreOwnership(new Map());
  }, []);

  // Get permissions for a specific store
  const getStorePermissions = useCallback((storeId) => {
    const userType = managementUser?.userType || managementUser?.user_type;
    if (userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin') {
      return new Set(['*']); // App admins have all permissions
    }
    return permissions.get(storeId) || new Set();
  }, [managementUser, permissions]);

  const isStoreOwner = useCallback((storeId) => {
    const userType = managementUser?.userType || managementUser?.user_type;
    if (userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin') {
      return false;
    }

    return storeOwnership.get(storeId) || false;
  }, [managementUser, storeOwnership]);

  // Load management data when management auth changes
  useEffect(() => {
    if (isManagementAuthenticated && managementUser) {
      loadStores();
      loadManagementPreferences();
    } else if (!isManagementAuthenticated) {
      setCurrentStoreIndex(0);
      setIsStoreSelectionHydrated(false);
      setStores([]);
      setStoresLoaded(false);
      setStoresError(null);
      setManagementPreferences(null);
      setManagementPreferencesLoaded(false);
      setManagementPreferencesError(null);
      permissionsInFlightRef.current = false;
      permissionsHydrationRetryRef.current = false;
      setPermissions(new Map());
      setStoreOwnership(new Map());
      setPermissionsLoaded(false);
      setPermissionsError(null);
    }
  }, [isManagementAuthenticated, managementUser, loadStores, loadManagementPreferences]);

  // Load customer data when customer auth changes
  useEffect(() => {
    if (isCustomerAuthenticated && customerUser) {
      loadCustomerPreferences();
    } else if (!isCustomerAuthenticated) {
      setCustomerPreferences(null);
      setCustomerPreferencesLoaded(false);
      setCustomerPreferencesError(null);
    }
  }, [isCustomerAuthenticated, customerUser, loadCustomerPreferences]);

  // Load permissions after stores are loaded (management only).
  useEffect(() => {
    if (
      isManagementAuthenticated
      && managementUser
      && storesLoaded
      && stores.length > 0
      && !permissionsLoaded
    ) {
      loadPermissions();
    }
  }, [
    isManagementAuthenticated,
    managementUser,
    storesLoaded,
    stores,
    loadPermissions,
    permissionsLoaded,
  ]);

  // After set-password auto-login, the first permissions call can fail before
  // currentStoreId exists. Retry once store selection has hydrated.
  useEffect(() => {
    if (
      !permissionsError
      || permissionsLoaded
      || !isStoreSelectionHydrated
      || !storesLoaded
      || stores.length === 0
      || !isManagementAuthenticated
      || !managementUser
    ) {
      return;
    }

    if (permissionsHydrationRetryRef.current) {
      return;
    }

    permissionsHydrationRetryRef.current = true;
    loadPermissions();
  }, [
    permissionsError,
    permissionsLoaded,
    isStoreSelectionHydrated,
    storesLoaded,
    stores.length,
    isManagementAuthenticated,
    managementUser,
    loadPermissions,
  ]);

  // Expose active preferences based on current page context
  const userPreferences = isCustomerPath() ? customerPreferences : managementPreferences;
  const preferencesLoading = isCustomerPath() ? customerPreferencesLoading : managementPreferencesLoading;
  const preferencesError = isCustomerPath() ? customerPreferencesError : managementPreferencesError;
  const preferencesLoaded = isCustomerPath() ? customerPreferencesLoaded : managementPreferencesLoaded;

  const value = {
    // Stores
    stores,
    storesLoading,
    storesError,
    storesLoaded,
    refreshStores,
    
    // Current store
    currentStore,
    currentStoreIndex,
    setCurrentStoreIndex,
    isStoreSelectionHydrated,

    // Active preferences (path-aware)
    userPreferences,
    preferencesLoading,
    preferencesError,
    preferencesLoaded,
    updateUserPreferences,
    refreshUserPreferences,
    updateUserProfilePicture,
    updateUserPhone,

    // Explicit preferences access
    managementPreferences,
    customerPreferences,
    
    // Permissions
    permissions,
    permissionsLoading,
    permissionsError,
    permissionsLoaded,
    getStorePermissions,
    isStoreOwner,
    refreshPermissions
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};
